
import {RMap} from "@e280/strata"
import {collect, hex, nap, need} from "@e280/stz"
import {Kv, IdbMagazine, idbOpen} from "@e280/kv"

import {consts} from "../../consts.js"
import {bankMigrations} from "./bank-migrations.js"
import {Id} from "../../lib/core/cryp/types.js"
import {seedId} from "../../lib/core/ergo/seed/seed-id.js"
import {Identity, DelegationRecord, IdentityTiming} from "../types.js"

export class Bank {
	static async init() {
		const idb = await idbOpen(consts.namespace)
		const magazine = new IdbMagazine(idb)
		const kv = new Kv(magazine)
		await bankMigrations(kv)
		const channel = new BroadcastChannel(`${consts.namespace}.bank`)
		const bank = new Bank(kv, () => channel.postMessage(Date.now()))
		await bank.load()
		channel.onmessage = () => nap(consts.broadcastReloadDelay).then(() => bank.load())
		return bank
	}

	#tables
	#onChange
	#identities = new RMap<Id, Identity>()

	constructor(kv: Kv, onChange: () => void) {
		this.#onChange = onChange
		this.#tables = {
			kv,
			identities: kv.scope<Identity>("identities"),
			identityTimings: kv.scope<IdentityTiming>("identityTimings"),
			delegations: kv.scope<DelegationRecord>("delegations"),
		}
	}

	get identities() {
		return [...this.#identities.values()]
	}

	getIdentity(id: Id) {
		return this.#identities.get(id)
	}

	requireIdentity(id: Id) {
		return need(this.#identities, id)
	}

	async load() {
		const identities = await collect(this.#tables.identities.entries())
		const identityTimings = new Map(await collect(this.#tables.identityTimings.entries()))
		identities.sort(([aId], [bId]) => {
			const a = identityTimings.get(aId)?.timeLastTouched ?? 0
			const b = identityTimings.get(bId)?.timeLastTouched ?? 0
			return b - a
		})
		this.#identities.clear()
		for (const [id, identity] of identities)
			this.#identities.set(id, identity)
	}

	async setIdentity(identity: Identity) {
		const id = seedId(identity.seed)
		const newTiming = await this.#touchIdentity(id)
		await this.#tables.kv.commit([
			this.#tables.identities.op.set(id, identity),
			this.#tables.identityTimings.op.set(id, newTiming)
		])
		this.#onChange()
		await this.load()
		navigator.storage.persist()
	}

	async recordDelegationEvent(delegation: DelegationRecord) {
		if (consts.enableDelegationLog) {
			const timestamp = delegation.time.toString().padStart(14, "0")
			const key = `${timestamp}-${hex.random(8)}`
			await this.#tables.delegations.set(key, delegation)
			this.#onChange()
		}
	}

	async deleteIdentity(id: Id) {
		await this.#tables.kv.commit([
			this.#tables.identities.op.delete(id),
			this.#tables.identityTimings.op.delete(id),
		])
		this.#onChange()
		await this.load()
	}

	async #touchIdentity(id: string) {
		const now = Date.now()
		const timing = await this.#tables.identityTimings.get(id)
			?? {id, timeFirstTouched: now, timeLastTouched: now}
		timing.timeLastTouched = now
		return timing
	}
}

